import { Component, computed, inject } from '@angular/core'
import { RouterLink } from '@angular/router'
import { AppSignals, DealerService } from '@appShared/services'
import { PageTitleComponent } from '@appShared/components/page-title/page-title.component'
import { RouteModel, SITE_DIRECTORY_ROUTES } from '@appShared/site-directory.routes'
import { GEN2_ROUTES } from '@appShared/gen2.routes'
import { EXTERNAL_URIS } from '@appShared/external.uris'

type AppLinkKind = 'googlePlay' | 'appStore' | 'web' | 'portal'

/** One app tile. Exactly one of externalUri / routerLink is set. */
interface AppLink {
   title: string
   description: string
   icon: string            // Font Awesome solid class
   kind: AppLinkKind       // drives the badge (store / web / portal download page)
   externalUri?: string
   routerLink?: string
}

interface AppLinkGroup {
   title: string
   links: AppLink[]
}

@Component({
    selector: 'app-app-links',
    imports: [PageTitleComponent, RouterLink],
    templateUrl: './app-links.component.html',
    styleUrls: ['./app-links.component.less']
})
export class AppLinksComponent {
   private _appSignals = inject(AppSignals)
   platformIsAndroid = this._appSignals.platformIsAndroid
   gen2Routes = GEN2_ROUTES
   external_uris = EXTERNAL_URIS
   private _siteDirectoryRoutes = SITE_DIRECTORY_ROUTES
   a4dAppRoute = this._siteDirectoryRoutes.a4d_app
   a4dAppRouteUri = `/${this.a4dAppRoute.uri}`
   captureAppRoute = this._siteDirectoryRoutes.capture_app
   captureAppRouteUri = `/${this.captureAppRoute.uri}`

   /** Badge text / icon per link kind. */
   readonly badges: Record<AppLinkKind, { label: string, icon: string }> = {
      googlePlay: { label: 'Google Play', icon: 'fa-brands fa-google-play' },
      appStore: { label: 'App Store', icon: 'fa-brands fa-app-store-ios' },
      web: { label: 'Web app', icon: 'fa-solid fa-globe' },
      portal: { label: 'Download', icon: 'fa-solid fa-download' }
   }

   /** Store links follow the device we detected; the list itself is the same either way. */
   linkGroups = computed<AppLinkGroup[]>(() => {
      const isAndroid = this.platformIsAndroid()
      const store = (android: RouteModel, ios: RouteModel): Pick<AppLink, 'kind' | 'externalUri'> =>
         isAndroid
            ? { kind: 'googlePlay', externalUri: android.uri }
            : { kind: 'appStore', externalUri: ios.uri }

      const uris = this.external_uris

      return [
         {
            title: 'For you and your team',
            links: [
               { title: uris.sales_rabbit_app_android.title, description: 'Map your area and track every door', icon: 'fa-map-location-dot', ...store(uris.sales_rabbit_app_android, uris.sales_rabbit_app_ios) },
               { title: this.a4dAppRoute.title, description: 'Orders, payments and customer records in the field', icon: 'fa-mobile-screen', kind: 'portal', routerLink: this.a4dAppRouteUri },
               { title: this.captureAppRoute.title, description: 'Snap and submit paperwork from your phone', icon: 'fa-camera', kind: 'portal', routerLink: this.captureAppRouteUri },
               { title: uris.e_goal_card.title, description: 'Set your goals and keep score each week', icon: 'fa-bullseye', kind: 'web', externalUri: uris.e_goal_card.uri },
               { title: uris.velocity_360.title, description: 'Manage your leads and follow-ups', icon: 'fa-gauge-high', kind: 'web', externalUri: uris.velocity_360.uri }
            ]
         },
         {
            title: 'For your customers',
            links: [
               { title: uris.adv_4_kids_app_android.title, description: 'Learning app for younger students', icon: 'fa-child-reaching', ...store(uris.adv_4_kids_app_android, uris.adv_4_kids_app_ios) },
               { title: uris.adv_4_teens_app_android.title, description: 'Study help for middle and high school', icon: 'fa-user-graduate', ...store(uris.adv_4_teens_app_android, uris.adv_4_teens_app_ios) },
               { title: uris.skwids_app_android.title, description: 'Games and activities for kids', icon: 'fa-puzzle-piece', ...store(uris.skwids_app_android, uris.skwids_app_ios) },
               { title: uris.lead_app_android.title, description: 'Leadership development for students', icon: 'fa-compass', ...store(uris.lead_app_android, uris.lead_app_ios) },
               { title: uris.homer_app_android.title, description: 'Early reading and learning for little ones', icon: 'fa-book-open-reader', ...store(uris.homer_app_android, uris.homer_app_ios) }
            ]
         }
      ]
   })

   goToExternal(uri?: string): void {
      this._appSignals.navigateToExternal(uri)
   }

   private _dealerService = inject(DealerService)
   goToPortal(route?: RouteModel): void {
      this._dealerService.navigateToGen2(route?.uri)
   }
}
